import { useState } from "react";
import { MessageFlagsClient, type FlagAdminSummary } from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync } from "../components/ui";

const flags = new MessageFlagsClient("");

/**
 * Keys that duplicate `severity`, which is the fixed platform axis. A tenant is free to define one
 * anyway — this is a warning, not a rule — but two competing urgency axes is how an inbox becomes
 * unpredictable, and the console says so when they try (§5.4).
 */
const SEVERITY_ECHOES = [
  "urgent", "critical", "important", "high", "highpriority", "high_priority",
  "priority", "warning", "info", "severe", "emergency", "asap",
];

const COLOR_HINTS = ["amber", "red", "blue", "green", "grey", "purple"];

/**
 * Console module: <b>Message flags</b>.
 *
 * The per-tenant flag vocabulary. One client's `action_required` is another's `recall_notice` and
 * a third's `safety_alert` — the grammar is fixed, the vocabulary is per client (§4.1, §5.4).
 */
export function MessageFlagsPage() {
  const { hasCapability } = usePermissions();
  const { data, error, loading, reload } = useAsync(() => flags.messageFlags_List());
  const [editing, setEditing] = useState<FlagAdminSummary | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const canAdmin = hasCapability("messages:admin");
  const tenantFlags = (data ?? []).filter((f) => !f.isPlatform);
  const platformFlags = (data ?? []).filter((f) => f.isPlatform);

  async function remove(key: string) {
    setActionError(null);
    try {
      await flags.messageFlags_Delete(key);
      setConfirming(null);
      setEditing(null);
      await reload();
    } catch (e) {
      setActionError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Message flags</h1>
          <p className="sub">
            Your own labels for marking messages. Flags are used for display, filtering and
            reporting only — they do not route, sort or gate anything. Use severity for urgency.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {actionError && <Alert kind="error">{actionError}</Alert>}

      {canAdmin && (
        <FlagEditor
          key={editing?.key ?? "new"}
          flag={editing}
          existing={data ?? []}
          onDone={() => { setEditing(null); void reload(); }}
          onCancel={editing ? () => setEditing(null) : undefined}
        />
      )}

      <Panel title="Your flags">
        {loading ? (
          <Empty>Loading…</Empty>
        ) : tenantFlags.length === 0 ? (
          <Empty>No flags defined. Add the labels your team already uses.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Key</th><th>Display name</th><th>Colour</th><th>Messages</th>
                <th>Description</th><th></th>
              </tr>
            </thead>
            <tbody>
              {tenantFlags.map((f) => (
                <tr key={f.key}>
                  <td className="mono">{f.key}</td>
                  <td>{f.displayName}</td>
                  <td>{f.colorHint ? <Chip>{f.colorHint}</Chip> : <span className="muted">—</span>}</td>
                  <td>{f.messageCount}</td>
                  <td className="muted">{f.description || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {canAdmin && (
                      <>
                        <button className="ghost small" onClick={() => setEditing(f)}>Edit</button>{" "}
                        <button className="danger small" onClick={() => setConfirming(f.key!)}>Remove</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {confirming && (
        <Panel title={`Remove "${confirming}"?`}>
          {(() => {
            const flag = tenantFlags.find((f) => f.key === confirming);
            const count = flag?.messageCount ?? 0;
            return count > 0 ? (
              <Alert kind="warn">
                {count} published {count === 1 ? "message carries" : "messages carry"} this flag.
                They keep it, but it will display as a bare key. New messages will not be able to
                use it.
              </Alert>
            ) : (
              <Alert kind="info">No message carries this flag. Removing it changes nothing else.</Alert>
            );
          })()}
          <div className="row">
            <button className="danger" onClick={() => remove(confirming)}>Remove it</button>
            <button className="ghost" onClick={() => setConfirming(null)}>Keep it</button>
          </div>
        </Panel>
      )}

      {platformFlags.length > 0 && (
        <Panel title="Platform flags">
          <table>
            <thead><tr><th>Key</th><th>Display name</th><th>Messages</th><th>Description</th></tr></thead>
            <tbody>
              {platformFlags.map((f) => (
                <tr key={f.key}>
                  <td className="mono">{f.key}</td>
                  <td>{f.displayName}</td>
                  <td>{f.messageCount}</td>
                  <td className="muted">{f.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            Platform flags are read-only here. Your own messages cannot use them.
          </p>
        </Panel>
      )}
    </>
  );
}

function FlagEditor({ flag, existing, onDone, onCancel }: {
  flag: FlagAdminSummary | null;
  existing: FlagAdminSummary[];
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [key, setKey] = useState(flag?.key ?? "");
  const [displayName, setDisplayName] = useState(flag?.displayName ?? "");
  const [colorHint, setColorHint] = useState(flag?.colorHint ?? "");
  const [description, setDescription] = useState(flag?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const normalized = key.trim().toLowerCase();
  const echoesSeverity = SEVERITY_ECHOES.includes(normalized.replace(/[\s-]/g, "_"))
    || SEVERITY_ECHOES.includes(normalized.replace(/[\s_-]/g, ""));
  const shadowsPlatform = !flag && existing.some((f) => f.isPlatform && f.key === normalized);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await flags.messageFlags_Upsert(normalized, {
        displayName,
        colorHint: colorHint || undefined,
        description: description || undefined,
      });
      onDone();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={flag ? `Edit flag — ${flag.key}` : "Add a flag"}
      actions={onCancel && <button className="ghost small" onClick={onCancel}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}

      {echoesSeverity && (
        <Alert kind="warn">
          <strong>{normalized}</strong> duplicates severity, which every message already carries as
          info, warning or critical. A flag with this name will not change priority. Name the
          situation instead — <span className="mono">recall_notice</span>,{" "}
          <span className="mono">safety_alert</span>,{" "}
          <span className="mono">stock_shortage</span> — and set severity on the message.
        </Alert>
      )}

      {shadowsPlatform && (
        <Alert kind="warn">
          A platform flag already uses the key <strong>{normalized}</strong>. Choose a different key
          so the two are not shown under the same name.
        </Alert>
      )}

      <div className="row">
        <Field label="Key">
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!!flag}
            placeholder="recall_notice"
          />
        </Field>
        <Field label="Display name">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Product recall"
          />
        </Field>
        <Field label="Colour hint">
          <select value={colorHint} onChange={(e) => setColorHint(e.target.value)}>
            <option value="">none</option>
            {COLOR_HINTS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <button onClick={submit} disabled={busy || !normalized || !displayName}>
          {flag ? "Save" : "Add flag"}
        </button>
      </div>

      <Field label="Description">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this flag means"
        />
      </Field>

      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        A flag does not create tracking. To require confirmation from recipients, set "requires
        acknowledgement" on the message itself.
      </p>
    </Panel>
  );
}
